/**
 * Frontend Tool Dispatcher — 前端工具调度器 (Architecture 3.0 Phase 4)
 *
 * 与后端 api/src/tools/dispatcher.ts 完全对称的前端工具注册 + 分发系统。
 * 将 AppLayout.tsx 中 550+ 行的 onToolRequest if-else 瀑布，
 * 替换为注册表驱动的 dispatchFrontendTool() 单点入口。
 *
 * 设计思想：
 * - 每个工具组注册自己的 handler
 * - dispatcher 负责查表 + 执行 + 返回结果文本
 * - AppLayout 只需拿到 responseText, 不再关心工具的具体执行逻辑
 */

import {
  scanDirectory,
  buildFileTreeListing,
  readFileByPath,
  renameFileByPath,
  createFolderAndMove,
  createLocalFolderByPath,
  moveLocalFiles,
  writeFileByPath,
  deleteFileByPath,
  readFileAsBase64,
  AiIndex,
} from '../hooks/useFileSystem';

// ─── 前端工具上下文 ─────────────────────────
export interface FrontendToolContext {
  /** 浏览器 File System Access API 的目录句柄 */
  localDirHandle: FileSystemDirectoryHandle;
  /** AI 索引对象（用于文件树标注） */
  aiIndex: AiIndex | null;
  /** 刷新本地文件树并同步到 AppLayout state 的回调 */
  refreshFileTree: () => Promise<void>;
  /** 更新当前 AI 消息的状态文本 */
  setMessageStatus: (status: string, content: string) => void;
  /** API 基础 URL */
  apiBaseUrl: string;
  /** 身份验证 token */
  authToken: string | null;
}

// ─── 工具处理器类型 ─────────────────────────
export type FrontendToolHandler = (
  args: Record<string, any>,
  ctx: FrontendToolContext,
) => Promise<string>;

// ─── 全局注册表 (Module-level Singleton) ────
const handlers = new Map<string, FrontendToolHandler>();

/**
 * 注册一个前端工具处理器
 */
export function registerFrontendTool(name: string, handler: FrontendToolHandler): void {
  handlers.set(name, handler);
}

/**
 * 分发前端工具调用
 * @returns 工具执行结果文本（总是返回字符串，不抛出异常）
 */
export async function dispatchFrontendTool(
  tool: string,
  args: Record<string, any>,
  ctx: FrontendToolContext,
): Promise<string> {
  const handler = handlers.get(tool);
  if (!handler) {
    return `[Error: Unknown tool "${tool}" or missing arguments]`;
  }
  try {
    return await handler(args, ctx);
  } catch (err: any) {
    return `[Error: Exception executing ${tool}: ${err?.message || err}]`;
  }
}

/**
 * 获取所有已注册的前端工具名称（调试用）
 */
export function getRegisteredFrontendTools(): string[] {
  return Array.from(handlers.keys());
}

// ─── 工具处理器的通用辅助函数 ──────────────

/**
 * 发送文件到后端 /api/file-reader/extract 统一提取文本
 */
export async function extractFileContent(
  localDirHandle: FileSystemDirectoryHandle,
  filePath: string,
  apiBaseUrl: string,
  authToken: string | null,
  question?: string,
): Promise<{ success: boolean; content: string; isMedia?: boolean; size: number }> {
  const fileData = await readFileAsBase64(localDirHandle, filePath);
  if (!fileData) {
    return { success: false, content: `Failed to read file ${filePath}`, size: 0 };
  }
  const extractResp = await fetch(`${apiBaseUrl}/api/file-reader/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({
      base64: fileData.base64,
      fileName: fileData.fileName,
      question,
    }),
  });
  if (!extractResp.ok) {
    return { success: false, content: `Extraction failed for ${filePath}: ${extractResp.statusText}`, size: fileData.size };
  }
  const result = await extractResp.json();
  if (!result.success) {
    return { success: false, content: result.error, size: fileData.size };
  }
  const label = result.isMedia ? 'Media Content Extracted' : '内容';
  return {
    success: true,
    content: `[${filePath} ${label} (${(fileData.size / 1024).toFixed(1)}KB)]:\n\n${result.content}`,
    isMedia: result.isMedia,
    size: fileData.size,
  };
}

/**
 * helper: 带 auth 的 POST 请求
 */
export async function authedPost(
  url: string,
  body: any,
  authToken: string | null,
): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

/**
 * helper: 触发浏览器下载 base64 文件
 */
export function triggerBase64Download(base64: string, mimeType: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = `data:${mimeType};base64,${base64}`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
